import type { CareTeamSwapLookups } from './CareTeamSwapService'

export type ProviderOption = CareTeamSwapLookups['providers'][number]

export interface RoleGroup {
  name: string
  abbr: string
  providers: ProviderOption[]
  // Clinical roles are license-checked against each member's state (from the backend lookups).
  clinical: boolean
}

// Short column/chip labels. The backend owns which roles appear and their order (lookups is limited
// to CARE_TEAM_SWAP_ROLE_NAMES); this map is display-only, falling back to the full role name.
const ROLE_ABBR: Record<string, string> = {
  Physician: 'MD',
  'Nurse Practitioner': 'NP',
  'Registered Nurse': 'RN',
  'Health Guide': 'HG',
  'Behavioral Health Specialist': 'BH',
  'Member Guide': 'MG',
}

export const roleAbbr = (roleName: string | null | undefined) =>
  (roleName && ROLE_ABBR[roleName]) || roleName || ''

/**
 * Group the lookup providers into per-role dropdowns, preserving the order the backend returns them
 * in (already limited and ordered to the reassignable care-team roles). Which roles are clinical
 * (license-checked) also comes from the backend lookups.
 */
export const deriveRoleGroups = (lookups: CareTeamSwapLookups): RoleGroup[] => {
  const clinicalRoles = new Set(lookups.clinical_role_names)
  const groups: RoleGroup[] = []
  const byName = new Map<string, RoleGroup>()
  for (const provider of lookups.providers) {
    const name = provider.internal_role?.role_name
    if (!name) continue
    let group = byName.get(name)
    if (!group) {
      group = { name, abbr: roleAbbr(name), providers: [], clinical: clinicalRoles.has(name) }
      byName.set(name, group)
      groups.push(group)
    }
    group.providers.push(provider)
  }
  return groups
}
